import type {
  AdminGuide,
  GuideAudience,
  GuideCategory,
  Risk,
} from "@/guides/adminGuides.types";

export const ADMIN_GUIDES_ROUTE_BASE = "/admin/guides";

export interface GuidesFilters {
  audiences: GuideAudience[];
  categories: GuideCategory[];
  risks: Risk[];
  search: string;
  showNotSupportedYet: boolean;
}

export const buildGuideSearchBlob = (guide: AdminGuide): string => {
  const troubleshootingText =
    guide.troubleshooting?.flatMap((item) => [item.title, ...item.body]) ?? [];
  const faqsText =
    guide.faqs?.flatMap((item) => [item.title, ...item.body]) ?? [];
  const decisionText =
    guide.decisionTable?.flatMap((row) => [
      row.situation,
      row.use,
      row.notes ?? "",
    ]) ?? [];

  return [
    guide.id,
    guide.title,
    guide.summary,
    guide.category,
    guide.location,
    guide.risk,
    guide.audience.join(" "),
    guide.keywords.join(" "),
    guide.whatThisHelpsYouDo.join(" "),
    guide.whenToUse.join(" "),
    guide.whenNotToUse.join(" "),
    guide.beforeYouStart.join(" "),
    guide.steps.join(" "),
    guide.warnings?.join(" ") ?? "",
    guide.safetyNotes?.join(" ") ?? "",
    guide.checklists
      ?.flatMap((checklist) => [checklist.title, ...checklist.items])
      .join(" ") ?? "",
    guide.whatSuccessLooksLike.join(" "),
    troubleshootingText.join(" "),
    faqsText.join(" "),
    guide.escalateWhen.join(" "),
    guide.sourceDocs.join(" "),
    decisionText.join(" "),
  ]
    .filter(Boolean)
    .join("\n")
    .toLowerCase();
};

const matchesGuideSearch = (guide: AdminGuide, search: string): boolean => {
  const normalizedSearch = search.trim().toLowerCase();
  if (normalizedSearch.length === 0) {
    return true;
  }

  const tokens = normalizedSearch.split(/\s+/).filter(Boolean);
  const blob = buildGuideSearchBlob(guide);
  return tokens.every((token) => blob.includes(token));
};

export const filterAdminGuides = (
  guides: AdminGuide[],
  filters: GuidesFilters,
): AdminGuide[] => {
  return guides
    .filter((guide) => {
      if (
        !filters.showNotSupportedYet &&
        guide.location === "NotSupportedYet"
      ) {
        return false;
      }

      if (
        filters.categories.length > 0 &&
        !filters.categories.includes(guide.category)
      ) {
        return false;
      }

      if (
        filters.audiences.length > 0 &&
        !guide.audience.some((audience) => filters.audiences.includes(audience))
      ) {
        return false;
      }

      if (filters.risks.length > 0 && !filters.risks.includes(guide.risk)) {
        return false;
      }

      return matchesGuideSearch(guide, filters.search);
    })
    .sort((left, right) => left.title.localeCompare(right.title));
};

export const toggleSelection = <TValue>(
  currentValues: TValue[],
  value: TValue,
): TValue[] => {
  return currentValues.includes(value)
    ? currentValues.filter((currentValue) => currentValue !== value)
    : [...currentValues, value];
};

export const countActiveGuideFilters = (filters: GuidesFilters): number => {
  return (
    filters.categories.length +
    filters.audiences.length +
    filters.risks.length +
    (filters.showNotSupportedYet ? 1 : 0)
  );
};

export const getGuideRiskTone = (
  risk: Risk,
): "danger" | "warning" | "success" => {
  if (risk === "high") {
    return "danger";
  }

  if (risk === "medium") {
    return "warning";
  }

  return "success";
};

export const getGuideLocationTone = (
  location: AdminGuide["location"],
): "accent" | "warning" | "default" => {
  if (location === "NotSupportedYet") {
    return "warning";
  }

  if (location === "Admin") {
    return "accent";
  }

  return "default";
};
